import React, { useState, useEffect } from 'react';
import { availabilityBlocksService, roomsService } from '../services/api';
import { toast } from 'react-toastify';
import { useIsMobile } from '../hooks';
import { Calendar as CalendarIcon, Trash2 as Trash2Icon } from 'lucide-react';

const BLOCK_TYPES = {
  MAINTENANCE: { label: 'Maintenance', color: '#B68235' },
  SUITE_BLOCK: { label: 'Suite Block', color: '#A9613C' },
  DEEP_CLEANING: { label: 'Deep Cleaning', color: '#1A3E2A' },
  RENOVATION: { label: 'Renovation', color: '#A13F2F' },
  SEASONAL_CLOSE: { label: 'Seasonal Close', color: '#5F6B61' },
  STAFF_USE: { label: 'Staff Use', color: '#1A3E2A' },
  CUSTOM: { label: 'Custom', color: '#A9613C' }
};

function AvailabilityBlockManager({ onBlockCreated, onClose, initialData }) {
  const isMobile = useIsMobile();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedBed, setSelectedBed] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [filteredBeds, setFilteredBeds] = useState([]);
  const [createMode, setCreateMode] = useState('single'); // 'single' or 'bulk'

  // Pagination and search state
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [searchRoomNumber, setSearchRoomNumber] = useState('');

  const [blockForm, setBlockForm] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    blockType: 'MAINTENANCE',
    roomId: '',
    bedId: '',
    blockLevel: 'room' // 'room' or 'bed'
  });

  // Bulk block form state
  const [bulkBlockForm, setBulkBlockForm] = useState({
    startDate: '',
    endDate: '',
    blockType: 'MAINTENANCE',
    description: '',
    selectedRooms: [], // Array of room IDs
    selectedBeds: []   // Array of bed IDs
  });

  useEffect(() => {
    // Check authentication before loading data
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('No authentication token found in AvailabilityBlockManager');
      toast.error('Authentication required. Please login to manage availability blocks.');
      return;
    }

    // Verify token is not expired
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const now = Date.now() / 1000;
      
      if (payload.exp < now) {
        console.warn('Token expired in AvailabilityBlockManager');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        toast.error('Session expired. Please login again.');
        return;
      }
      
      console.log('Token is valid for AvailabilityBlockManager:', payload.email);
    } catch (error) {
      console.error('Invalid token format in AvailabilityBlockManager:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      toast.error('Invalid authentication token. Please login again.');
      return;
    }

    loadBlocks();
    loadRooms();
  }, []);

  // Reload blocks when search or sort changes
  useEffect(() => {
    if (rooms.length > 0) {
      loadBlocks(currentPage);
    }
  }, [searchRoomNumber, currentPage]);

  useEffect(() => {
    if (blockForm.roomId && blockForm.blockLevel === 'bed') {
      const room = rooms.find(r => r.id === blockForm.roomId);
      setFilteredBeds(room?.beds || []);
    } else {
      setFilteredBeds([]);
      setBlockForm(prev => ({ ...prev, bedId: '' }));
    }
  }, [blockForm.roomId, blockForm.blockLevel, rooms]);

  // Handle initial data from calendar empty cell click
  useEffect(() => {
    if (initialData && rooms.length > 0) {
      console.log('Auto-filling block form with initial data:', initialData);
      
      // Update the block form with initial data
      setBlockForm(prev => ({
        ...prev,
        roomId: initialData.roomId || '',
        bedId: initialData.bedId || '',
        startDate: initialData.startDate || '',
        endDate: initialData.startDate || '', // Default end date to same as start
        blockLevel: initialData.blockLevel || 'room'
      }));
      
      // Auto-open the create form
      setShowCreateForm(true);
      setCreateMode('single');
      
      // If bed is specified, ensure we load the filtered beds
      if (initialData.bedId && initialData.roomId) {
        const room = rooms.find(r => r.id === initialData.roomId);
        setFilteredBeds(room?.beds || []);
      }
    }
  }, [initialData, rooms]);

  const loadBlocks = async (page = 1) => {
    try {
      setLoading(true);
      
      // Build query parameters
      const params = {
        page: page,
        limit: 20,
        isActive: true
      };

      // Add room number search if provided
      if (searchRoomNumber.trim()) {
        // Find room IDs that match the search
        const matchingRooms = rooms.filter(room => 
          room.roomNumber?.toLowerCase().includes(searchRoomNumber.toLowerCase())
        );
        
        // If we have matching rooms, fetch blocks for those rooms
        if (matchingRooms.length > 0) {
          // Fetch blocks for each matching room and combine
          const allBlocks = [];
          let combinedPagination = null;
          
          for (const room of matchingRooms) {
            const response = await availabilityBlocksService.getAll({
              ...params,
              roomId: room.id
            });
            
            const roomBlocks = response.data?.blocks || [];
            allBlocks.push(...roomBlocks);
            
            // Use pagination from first response
            if (!combinedPagination && response.data?.pagination) {
              combinedPagination = response.data.pagination;
            }
          }
          
          // Sort blocks by start date
          const sortedBlocks = sortBlocksByStartDate(allBlocks);
          
          setBlocks(sortedBlocks);
          setPagination(combinedPagination);
        } else {
          // No matching rooms found
          setBlocks([]);
          setPagination(null);
        }
      } else {
        // Fetch all blocks without room filter
        const response = await availabilityBlocksService.getAll(params);
        
        const fetchedBlocks = response.data?.blocks || [];
        
        // Sort blocks by start date
        const sortedBlocks = sortBlocksByStartDate(fetchedBlocks);
        
        setBlocks(sortedBlocks);
        setPagination(response.data?.pagination || null);
      }
    } catch (error) {
      console.error('Error loading blocks:', error);
      toast.error('Failed to load availability blocks');
      setBlocks([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to sort blocks by start date (ALWAYS ascending)
  const sortBlocksByStartDate = (blocksArray) => {
    return [...blocksArray].sort((a, b) => {
      const dateA = new Date(a.startDate);
      const dateB = new Date(b.startDate);
      // Always sort in ascending order (oldest first)
      return dateA - dateB;
    });
  };

  const loadRooms = async () => {
    try {
      console.log('Loading rooms for availability blocks...');
      const response = await roomsService.getAll({ 
        limit: 100, 
        page: 1,
        include: 'beds,roomType' // Include related data
      });
      
      // Handle different response formats
      const roomsData = response.data?.rooms || response.rooms || response || [];
      console.log(`Loaded ${roomsData.length} rooms (total: ${response.data?.total || 'unknown'})`);
      
      setRooms(roomsData);
      
      if (roomsData.length === 0) {
        console.warn('No rooms found in the system');
        toast.warning('No rooms found in the system. Please add rooms first.');
      }
    } catch (error) {
      console.error('Error loading rooms:', error);
      toast.error(`Failed to load rooms: ${error.message || 'Unknown error'}. Please check your connection and authentication.`);
      setRooms([]);
    }
  };

  const handleCreateBlock = async (e) => {
    e.preventDefault();
    
    if (!blockForm.name.trim()) {
      toast.error('Block name is required');
      return;
    }

    if (!blockForm.startDate || !blockForm.endDate) {
      toast.error('Start and end dates are required');
      return;
    }

    if (new Date(blockForm.startDate) >= new Date(blockForm.endDate)) {
      toast.error('End date must be after start date');
      return;
    }

    if (blockForm.blockLevel === 'room' && !blockForm.roomId) {
      toast.error('Please select a room');
      return;
    }

    if (blockForm.blockLevel === 'bed' && (!blockForm.roomId || !blockForm.bedId)) {
      toast.error('Please select a room and bed');
      return;
    }

    try {
      setLoading(true);
      
      const blockData = {
        name: blockForm.name.trim(),
        description: blockForm.description.trim() || undefined,
        startDate: blockForm.startDate,
        endDate: blockForm.endDate,
        blockType: blockForm.blockType,
        roomId: blockForm.blockLevel === 'room' ? blockForm.roomId : undefined,
        bedId: blockForm.blockLevel === 'bed' ? blockForm.bedId : undefined,
      };

      await availabilityBlocksService.create(blockData);
      
      toast.success('Availability block created successfully');
      resetForm();
      setShowCreateForm(false);
      loadBlocks();
      
      if (onBlockCreated) {
        onBlockCreated();
      }
    } catch (error) {
      console.error('Error creating block:', error);
      toast.error(error.message || 'Failed to create availability block');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkCreateBlocks = async (e) => {
    e.preventDefault();
    
    if (!bulkBlockForm.startDate || !bulkBlockForm.endDate) {
      toast.error('Start and end dates are required');
      return;
    }

    if (new Date(bulkBlockForm.startDate) >= new Date(bulkBlockForm.endDate)) {
      toast.error('End date must be after start date');
      return;
    }

    const totalSelections = bulkBlockForm.selectedRooms.length + bulkBlockForm.selectedBeds.length;
    
    if (totalSelections === 0) {
      toast.error('Please select at least one room or bed');
      return;
    }

    if (!window.confirm(`Create ${totalSelections} maintenance block(s) from ${bulkBlockForm.startDate} to ${bulkBlockForm.endDate}?`)) {
      return;
    }

    try {
      setLoading(true);
      let successCount = 0;
      let failCount = 0;
      const errors = [];

      // Create blocks for selected rooms
      for (const roomId of bulkBlockForm.selectedRooms) {
        try {
          const room = rooms.find(r => r.id === roomId);
          const blockData = {
            name: `${BLOCK_TYPES[bulkBlockForm.blockType].label} - Room ${room?.roomNumber || roomId}`,
            description: bulkBlockForm.description.trim() || `Bulk ${BLOCK_TYPES[bulkBlockForm.blockType].label.toLowerCase()}`,
            startDate: bulkBlockForm.startDate,
            endDate: bulkBlockForm.endDate,
            blockType: bulkBlockForm.blockType,
            roomId: roomId,
          };

          await availabilityBlocksService.create(blockData);
          successCount++;
        } catch (error) {
          failCount++;
          errors.push(`Room ${roomId}: ${error.message}`);
          console.error(`Error creating block for room ${roomId}:`, error);
        }
      }

      // Create blocks for selected beds
      for (const bedId of bulkBlockForm.selectedBeds) {
        try {
          // Find the bed in the rooms data
          let bedInfo = null;
          let roomInfo = null;
          for (const room of rooms) {
            const bed = room.beds?.find(b => b.id === bedId);
            if (bed) {
              bedInfo = bed;
              roomInfo = room;
              break;
            }
          }

          const blockData = {
            name: `${BLOCK_TYPES[bulkBlockForm.blockType].label} - Room ${roomInfo?.roomNumber || ''} Bed ${bedInfo?.bedNumber || bedId}`,
            description: bulkBlockForm.description.trim() || `Bulk ${BLOCK_TYPES[bulkBlockForm.blockType].label.toLowerCase()}`,
            startDate: bulkBlockForm.startDate,
            endDate: bulkBlockForm.endDate,
            blockType: bulkBlockForm.blockType,
            bedId: bedId,
          };

          await availabilityBlocksService.create(blockData);
          successCount++;
        } catch (error) {
          failCount++;
          errors.push(`Bed ${bedId}: ${error.message}`);
          console.error(`Error creating block for bed ${bedId}:`, error);
        }
      }

      // Show results
      if (successCount > 0) {
        toast.success(`Successfully created ${successCount} availability block(s)`);
      }
      
      if (failCount > 0) {
        toast.error(`Failed to create ${failCount} block(s). Check console for details.`);
        console.error('Bulk creation errors:', errors);
      }

      // Reset and reload
      resetBulkForm();
      setShowCreateForm(false);
      loadBlocks();
      
      if (onBlockCreated) {
        onBlockCreated();
      }
    } catch (error) {
      console.error('Error in bulk block creation:', error);
      toast.error('Failed to create bulk availability blocks');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBlock = async (blockId, blockName) => {
    if (!window.confirm(`Are you sure you want to delete the block "${blockName}"?`)) {
      return;
    }

    try {
      await availabilityBlocksService.delete(blockId);
      toast.success('Availability block deleted successfully');
      loadBlocks();
      
      if (onBlockCreated) {
        onBlockCreated();
      }
    } catch (error) {
      console.error('Error deleting block:', error);
      toast.error('Failed to delete availability block');
    }
  };

  const resetForm = () => {
    setBlockForm({
      name: '',
      description: '',
      startDate: '',
      endDate: '',
      blockType: 'MAINTENANCE',
      roomId: '',
      bedId: '',
      blockLevel: 'room'
    });
  };

  const resetBulkForm = () => {
    setBulkBlockForm({
      startDate: '',
      endDate: '',
      blockType: 'MAINTENANCE',
      description: '',
      selectedRooms: [],
      selectedBeds: []
    });
  };

  const toggleRoomSelection = (roomId) => {
    setBulkBlockForm(prev => ({
      ...prev,
      selectedRooms: prev.selectedRooms.includes(roomId)
        ? prev.selectedRooms.filter(id => id !== roomId)
        : [...prev.selectedRooms, roomId]
    }));
  };

  const toggleBedSelection = (bedId) => {
    setBulkBlockForm(prev => ({
      ...prev,
      selectedBeds: prev.selectedBeds.includes(bedId)
        ? prev.selectedBeds.filter(id => id !== bedId)
        : [...prev.selectedBeds, bedId]
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getBlockTypeInfo = (blockType) => {
    return BLOCK_TYPES[blockType] || BLOCK_TYPES.CUSTOM;
  };

  const getRoomDisplayName = (block) => {
    if (block.room) {
      return `Room ${block.room.roomNumber} (${block.room.roomType?.name})`;
    }
    if (block.bed) {
      return `Room ${block.bed.room.roomNumber} - Bed ${block.bed.bedNumber}`;
    }
    return 'Unknown';
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: isMobile ? '0' : '20px'
    }}>
      <div style={{
        backgroundColor: '#E9DFCE',
        borderRadius: isMobile ? '0' : '16px',
        width: isMobile ? '100%' : '90%',
        maxWidth: isMobile ? '100%' : '900px',
        height: isMobile ? '100vh' : 'auto',
        maxHeight: isMobile ? '100vh' : '90vh',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'flex-start' : 'center',
          padding: isMobile ? '16px' : '24px',
          gap: '12px',
          flexWrap: 'wrap',
          borderBottom: '2px solid #EEEBE1'
        }}>
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '800', color: '#24332A' }}>
            Availability Block Management
          </h2>
          <button
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              fontSize: '28px',
              cursor: 'pointer',
              color: '#5F6B61',
              padding: '5px'
            }}
          >
            ×
          </button>
        </div>

        <div style={{ height: isMobile ? 'calc(100vh - 82px)' : 'calc(90vh - 100px)', overflow: 'auto' }}>
          {/* Action Buttons */}
          <div style={{ padding: '24px', borderBottom: '1px solid #E3DFD3' }}>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                onClick={() => {
                  setShowCreateForm(!showCreateForm);
                  if (!showCreateForm) {
                    resetForm();
                    resetBulkForm();
                  }
                }}
                style={{
                  backgroundColor: showCreateForm ? '#5F6B61' : '#1A3E2A',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {showCreateForm ? 'Cancel' : 'Create New Block'}
              </button>

              {showCreateForm && (
                <div style={{ 
                  display: 'flex', 
                  gap: '8px', 
                  backgroundColor: '#EEEBE1', 
                  padding: '4px', 
                  borderRadius: '8px' 
                }}>
                  <button
                    onClick={() => setCreateMode('single')}
                    style={{
                      backgroundColor: createMode === 'single' ? '#1A3E2A' : 'transparent',
                      color: createMode === 'single' ? 'white' : '#4A564D',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Single Block
                  </button>
                  <button
                    onClick={() => setCreateMode('bulk')}
                    style={{
                      backgroundColor: createMode === 'bulk' ? '#1A3E2A' : 'transparent',
                      color: createMode === 'bulk' ? 'white' : '#4A564D',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Bulk Blocks
                  </button>
                </div>
              )}
              
              <button
                onClick={loadRooms}
                disabled={loading}
                style={{
                  backgroundColor: loading ? '#A5A89B' : '#1A3E2A',
                  color: 'white',
                  border: 'none',
                  padding: '12px 18px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                {loading ? '' : ''} Reload Rooms ({rooms.length})
              </button>
            </div>
          </div>

          {/* Create Form */}
          {showCreateForm && createMode === 'single' && (
            <div style={{ padding: '24px', backgroundColor: '#F5F3EB', borderBottom: '1px solid #E3DFD3' }}>
              <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '700', color: '#36433B' }}>
                Create Single Availability Block
              </h3>
              
              <form onSubmit={handleCreateBlock}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '20px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#36433B', marginBottom: '6px' }}>
                      Block Name *
                    </label>
                    <input
                      type="text"
                      value={blockForm.name}
                      onChange={(e) => setBlockForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Room 101 Maintenance"
                      style={{
                        width: '100%',
                        padding: '10px',
                        fontSize: '14px',
                        border: '1px solid #D2CDBF',
                        borderRadius: '6px'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#36433B', marginBottom: '6px' }}>
                      Block Type
                    </label>
                    <select
                      value={blockForm.blockType}
                      onChange={(e) => setBlockForm(prev => ({ ...prev, blockType: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px',
                        fontSize: '14px',
                        border: '1px solid #D2CDBF',
                        borderRadius: '6px'
                      }}
                    >
                      {Object.entries(BLOCK_TYPES).map(([key, info]) => (
                        <option key={key} value={key}>
                          {info.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#36433B', marginBottom: '6px' }}>
                      Block Level
                    </label>
                    <select
                      value={blockForm.blockLevel}
                      onChange={(e) => setBlockForm(prev => ({ ...prev, blockLevel: e.target.value, bedId: '' }))}
                      style={{
                        width: '100%',
                        padding: '10px',
                        fontSize: '14px',
                        border: '1px solid #D2CDBF',
                        borderRadius: '6px'
                      }}
                    >
                      <option value="room">Entire Room</option>
                      <option value="bed">Single Bed</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '20px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#36433B', marginBottom: '6px' }}>
                      Start Date *
                    </label>
                    <input
                      type="date"
                      value={blockForm.startDate}
                      onChange={(e) => setBlockForm(prev => ({ ...prev, startDate: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px',
                        fontSize: '14px',
                        border: '1px solid #D2CDBF',
                        borderRadius: '6px'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#36433B', marginBottom: '6px' }}>
                      End Date *
                    </label>
                    <input
                      type="date"
                      value={blockForm.endDate}
                      onChange={(e) => setBlockForm(prev => ({ ...prev, endDate: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px',
                        fontSize: '14px',
                        border: '1px solid #D2CDBF',
                        borderRadius: '6px'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#36433B', marginBottom: '6px' }}>
                      Select Room *
                    </label>
                    <select
                      value={blockForm.roomId}
                      onChange={(e) => setBlockForm(prev => ({ ...prev, roomId: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px',
                        fontSize: '14px',
                        border: '1px solid #D2CDBF',
                        borderRadius: '6px'
                      }}
                    >
                      <option value="">Select a room...</option>
                      {rooms.map(room => (
                        <option key={room.id} value={room.id}>
                          Room {room.roomNumber} - {room.roomType?.name} ({room.beds?.length || 0} beds)
                        </option>
                      ))}
                    </select>
                    {rooms.length === 0 && (
                      <div style={{ marginTop: '5px', fontSize: '12px', color: '#A13F2F' }}>
                        No rooms available. Check authentication and API connection.
                        <button
                          type="button"
                          onClick={loadRooms}
                          disabled={loading}
                          style={{
                            marginLeft: '10px',
                            backgroundColor: '#1A3E2A',
                            color: 'white',
                            border: 'none',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            cursor: 'pointer'
                          }}
                        >
                          Retry
                        </button>
                      </div>
                    )}
                    {rooms.length > 0 && (
                      <div style={{ marginTop: '5px', fontSize: '12px', color: '#1A3E2A' }}>
                        {rooms.length} rooms loaded successfully
                      </div>
                    )}
                  </div>
                </div>

                {blockForm.blockLevel === 'bed' && filteredBeds.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#36433B', marginBottom: '6px' }}>
                      Select Bed *
                    </label>
                    <select
                      value={blockForm.bedId}
                      onChange={(e) => setBlockForm(prev => ({ ...prev, bedId: e.target.value }))}
                      style={{
                        width: '100%',
                        maxWidth: '300px',
                        padding: '10px',
                        fontSize: '14px',
                        border: '1px solid #D2CDBF',
                        borderRadius: '6px'
                      }}
                    >
                      <option value="">Select a bed...</option>
                      {filteredBeds.map(bed => (
                        <option key={bed.id} value={bed.id}>
                          Bed {bed.bedNumber} {bed.isFemaleOnly ? '(Female Only)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#36433B', marginBottom: '6px' }}>
                    Description (Optional)
                  </label>
                  <textarea
                    value={blockForm.description}
                    onChange={(e) => setBlockForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Additional details about this block..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '10px',
                      fontSize: '14px',
                      border: '1px solid #D2CDBF',
                      borderRadius: '6px',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    backgroundColor: loading ? '#A5A89B' : '#1A3E2A',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: loading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {loading ? 'Creating...' : 'Create Block'}
                </button>
              </form>
            </div>
          )}

          {/* Bulk Create Form */}
          {showCreateForm && createMode === 'bulk' && (
            <div style={{ padding: '24px', backgroundColor: '#F0F3EE', borderBottom: '1px solid #E3DFD3' }}>
              <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '700', color: '#36433B' }}>
                Create Bulk Availability Blocks
              </h3>
              
              <form onSubmit={handleBulkCreateBlocks}>
                {/* Date Range and Type Selection */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '20px', padding: '16px', backgroundColor: 'white', borderRadius: '8px', border: '2px solid #1A3E2A' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#36433B', marginBottom: '6px' }}>
                      Start Date *
                    </label>
                    <input
                      type="date"
                      value={bulkBlockForm.startDate}
                      onChange={(e) => setBulkBlockForm(prev => ({ ...prev, startDate: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px',
                        fontSize: '14px',
                        border: '1px solid #D2CDBF',
                        borderRadius: '6px'
                      }}
                      required
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#36433B', marginBottom: '6px' }}>
                      End Date *
                    </label>
                    <input
                      type="date"
                      value={bulkBlockForm.endDate}
                      onChange={(e) => setBulkBlockForm(prev => ({ ...prev, endDate: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px',
                        fontSize: '14px',
                        border: '1px solid #D2CDBF',
                        borderRadius: '6px'
                      }}
                      required
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#36433B', marginBottom: '6px' }}>
                      Block Type
                    </label>
                    <select
                      value={bulkBlockForm.blockType}
                      onChange={(e) => setBulkBlockForm(prev => ({ ...prev, blockType: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px',
                        fontSize: '14px',
                        border: '1px solid #D2CDBF',
                        borderRadius: '6px'
                      }}
                    >
                      {Object.entries(BLOCK_TYPES).map(([key, info]) => (
                        <option key={key} value={key}>
                          {info.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#36433B', marginBottom: '6px' }}>
                      Description (Optional)
                    </label>
                    <input
                      type="text"
                      value={bulkBlockForm.description}
                      onChange={(e) => setBulkBlockForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="e.g., Annual deep cleaning maintenance"
                      style={{
                        width: '100%',
                        padding: '10px',
                        fontSize: '14px',
                        border: '1px solid #D2CDBF',
                        borderRadius: '6px'
                      }}
                    />
                  </div>
                </div>

                {/* Room Selection */}
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#36433B', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Select Rooms ({bulkBlockForm.selectedRooms.length} selected)
                  </h4>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', 
                    gap: '12px',
                    maxHeight: '200px',
                    overflow: 'auto',
                    padding: '12px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: '1px solid #D2CDBF'
                  }}>
                    {rooms.length === 0 ? (
                      <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px', color: '#5F6B61' }}>
                        No rooms available
                      </div>
                    ) : (
                      rooms.map(room => (
                        <label
                          key={room.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '10px',
                            border: `2px solid ${bulkBlockForm.selectedRooms.includes(room.id) ? '#1A3E2A' : '#E3DFD3'}`,
                            borderRadius: '6px',
                            cursor: 'pointer',
                            backgroundColor: bulkBlockForm.selectedRooms.includes(room.id) ? '#E1E9E2' : 'white',
                            transition: 'all 0.2s'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={bulkBlockForm.selectedRooms.includes(room.id)}
                            onChange={() => toggleRoomSelection(room.id)}
                            style={{ cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: '14px', fontWeight: '500' }}>
                            Room {room.roomNumber}
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                </div>

                {/* Bed Selection */}
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#36433B', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Select Beds ({bulkBlockForm.selectedBeds.length} selected)
                  </h4>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
                    gap: '12px',
                    maxHeight: '250px',
                    overflow: 'auto',
                    padding: '12px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: '1px solid #D2CDBF'
                  }}>
                    {rooms.length === 0 ? (
                      <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px', color: '#5F6B61' }}>
                        No rooms/beds available
                      </div>
                    ) : (
                      rooms.flatMap(room => 
                        (room.beds || []).map(bed => (
                          <label
                            key={bed.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '10px',
                              border: `2px solid ${bulkBlockForm.selectedBeds.includes(bed.id) ? '#1A3E2A' : '#E3DFD3'}`,
                              borderRadius: '6px',
                              cursor: 'pointer',
                              backgroundColor: bulkBlockForm.selectedBeds.includes(bed.id) ? '#E1E9E2' : 'white',
                              transition: 'all 0.2s'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={bulkBlockForm.selectedBeds.includes(bed.id)}
                              onChange={() => toggleBedSelection(bed.id)}
                              style={{ cursor: 'pointer' }}
                            />
                            <span style={{ fontSize: '14px', fontWeight: '500' }}>
                              Room {room.roomNumber} - Bed {bed.bedNumber}
                            </span>
                          </label>
                        ))
                      )
                    )}
                  </div>
                </div>

                {/* Summary and Submit */}
                <div style={{ 
                  padding: '16px', 
                  backgroundColor: '#FAF4E8', 
                  border: '2px solid #C9903C',
                  borderRadius: '8px',
                  marginBottom: '16px'
                }}>
                  <div style={{ fontWeight: '600', color: '#684A1F', marginBottom: '8px' }}>
                    Summary
                  </div>
                  <div style={{ fontSize: '14px', color: '#4F3818' }}>
                    • {bulkBlockForm.selectedRooms.length} room(s) selected<br/>
                    • {bulkBlockForm.selectedBeds.length} bed(s) selected<br/>
                    • Total blocks to create: <strong>{bulkBlockForm.selectedRooms.length + bulkBlockForm.selectedBeds.length}</strong>
                    {bulkBlockForm.startDate && bulkBlockForm.endDate && (
                      <>
                        <br/>• Period: {new Date(bulkBlockForm.startDate).toLocaleDateString()} - {new Date(bulkBlockForm.endDate).toLocaleDateString()}
                      </>
                    )}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || (bulkBlockForm.selectedRooms.length === 0 && bulkBlockForm.selectedBeds.length === 0)}
                  style={{
                    width: '100%',
                    backgroundColor: loading || (bulkBlockForm.selectedRooms.length === 0 && bulkBlockForm.selectedBeds.length === 0) ? '#A5A89B' : '#1A3E2A',
                    color: 'white',
                    border: 'none',
                    padding: '14px 24px',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '700',
                    cursor: loading || (bulkBlockForm.selectedRooms.length === 0 && bulkBlockForm.selectedBeds.length === 0) ? 'not-allowed' : 'pointer'
                  }}
                >
                  {loading ? 'Creating Bulk Blocks...' : `Create ${bulkBlockForm.selectedRooms.length + bulkBlockForm.selectedBeds.length} Block(s)`}
                </button>
              </form>
            </div>
          )}

          {/* Blocks List */}
          <div style={{ padding: '24px' }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '700', color: '#36433B' }}>
              Active Blocks ({pagination?.totalItems || blocks.length})
            </h3>

            {/* Search and Sort Controls */}
            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              marginBottom: '20px',
              flexWrap: 'wrap',
              alignItems: 'center'
            }}>
              {/* Room Number Search */}
              <div style={{ flex: '1 1 300px' }}>
                <input
                  type="text"
                  placeholder="Search by room number..."
                  value={searchRoomNumber}
                  onChange={(e) => {
                    setSearchRoomNumber(e.target.value);
                    setCurrentPage(1); // Reset to first page on search
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: '2px solid #E3DFD3',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'all 0.2s'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#1A3E2A';
                    e.target.style.boxShadow = '0 0 0 3px rgba(26, 62, 42, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#E3DFD3';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Sort Info Label */}
              <div style={{ 
                display: 'flex', 
                gap: '8px', 
                alignItems: 'center',
                padding: '10px 16px',
                backgroundColor: '#F0F3EE',
                borderRadius: '8px',
                border: '2px solid #1A3E2A'
              }}>
                <span style={{ fontSize: '14px', color: '#0F2619', fontWeight: '600' }}>
                  Sorted by Start Date (Oldest First)
                </span>
              </div>

              {/* Clear Search */}
              {searchRoomNumber && (
                <button
                  onClick={() => {
                    setSearchRoomNumber('');
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: '10px 16px',
                    backgroundColor: '#A13F2F',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#8E3628';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#A13F2F';
                  }}
                >
                  ✕ Clear
                </button>
              )}
            </div>

            {loading && !showCreateForm ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#5F6B61' }}>
                Loading blocks...
              </div>
            ) : blocks.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#5F6B61' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}><CalendarIcon size="1em" aria-hidden="true" /></div>
                <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>No active blocks</div>
                <div>Create a block to start managing room/bed availability</div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: '16px' }}>
                {blocks.map(block => {
                  const typeInfo = getBlockTypeInfo(block.blockType);
                  return (
                    <div
                      key={block.id}
                      style={{
                        border: `2px solid ${typeInfo.color}`,
                        borderRadius: '12px',
                        padding: '16px',
                        backgroundColor: `${typeInfo.color}10`
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div>
                            <div style={{ fontWeight: '700', fontSize: '16px', color: '#24332A' }}>
                              {block.name}
                            </div>
                            <div style={{ fontSize: '12px', color: typeInfo.color, fontWeight: '600' }}>
                              {typeInfo.label}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteBlock(block.id, block.name)}
                          style={{
                            backgroundColor: '#A13F2F',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '6px 8px',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                        >
                          <Trash2Icon size="1em" aria-hidden="true" />
                        </button>
                      </div>

                      <div style={{ fontSize: '14px', color: '#4A564D', marginBottom: '8px' }}>
                        <strong>Location:</strong> {getRoomDisplayName(block)}
                      </div>

                      <div style={{ fontSize: '14px', color: '#4A564D', marginBottom: '8px' }}>
                        <strong>Period:</strong> {formatDate(block.startDate)} - {formatDate(block.endDate)}
                      </div>

                      {block.description && (
                        <div style={{ fontSize: '14px', color: '#4A564D', marginBottom: '8px' }}>
                          <strong>Notes:</strong> {block.description}
                        </div>
                      )}

                      {block.createdByUser && (
                        <div style={{ fontSize: '12px', color: '#5F6B61', marginTop: '12px' }}>
                          Created by: {block.createdByUser.name || block.createdByUser.email}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination Controls */}
            {pagination && pagination.totalPages > 1 && (
              <div style={{ 
                marginTop: '24px',
                padding: '16px',
                backgroundColor: '#F5F3EB',
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '12px'
              }}>
                {/* Page Info */}
                <div style={{ fontSize: '14px', color: '#5F6B61', fontWeight: '600' }}>
                  Page {pagination.currentPage} of {pagination.totalPages}
                  <span style={{ marginLeft: '8px', opacity: 0.8 }}>
                    ({pagination.totalItems} total blocks)
                  </span>
                </div>

                {/* Pagination Buttons */}
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {/* First Page */}
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={!pagination.hasPreviousPage}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: pagination.hasPreviousPage ? '#1A3E2A' : '#E3DFD3',
                      color: pagination.hasPreviousPage ? 'white' : '#A5A89B',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: pagination.hasPreviousPage ? 'pointer' : 'not-allowed',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (pagination.hasPreviousPage) {
                        e.target.style.backgroundColor = '#163626';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (pagination.hasPreviousPage) {
                        e.target.style.backgroundColor = '#1A3E2A';
                      }
                    }}
                  >
                    First
                  </button>

                  {/* Previous Page */}
                  <button
                    onClick={() => setCurrentPage(prev => prev - 1)}
                    disabled={!pagination.hasPreviousPage}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: pagination.hasPreviousPage ? '#1A3E2A' : '#E3DFD3',
                      color: pagination.hasPreviousPage ? 'white' : '#A5A89B',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: pagination.hasPreviousPage ? 'pointer' : 'not-allowed',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (pagination.hasPreviousPage) {
                        e.target.style.backgroundColor = '#163626';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (pagination.hasPreviousPage) {
                        e.target.style.backgroundColor = '#1A3E2A';
                      }
                    }}
                  >
                    ← Previous
                  </button>

                  {/* Page Numbers */}
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                      let pageNum;
                      if (pagination.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (pagination.currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (pagination.currentPage >= pagination.totalPages - 2) {
                        pageNum = pagination.totalPages - 4 + i;
                      } else {
                        pageNum = pagination.currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          style={{
                            padding: '8px 12px',
                            backgroundColor: pagination.currentPage === pageNum ? '#1A3E2A' : 'white',
                            color: pagination.currentPage === pageNum ? 'white' : '#36433B',
                            border: `2px solid ${pagination.currentPage === pageNum ? '#1A3E2A' : '#E3DFD3'}`,
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            minWidth: '40px',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            if (pagination.currentPage !== pageNum) {
                              e.target.style.backgroundColor = '#EEEBE1';
                              e.target.style.borderColor = '#1A3E2A';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (pagination.currentPage !== pageNum) {
                              e.target.style.backgroundColor = 'white';
                              e.target.style.borderColor = '#E3DFD3';
                            }
                          }}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  {/* Next Page */}
                  <button
                    onClick={() => setCurrentPage(prev => prev + 1)}
                    disabled={!pagination.hasNextPage}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: pagination.hasNextPage ? '#1A3E2A' : '#E3DFD3',
                      color: pagination.hasNextPage ? 'white' : '#A5A89B',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: pagination.hasNextPage ? 'pointer' : 'not-allowed',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (pagination.hasNextPage) {
                        e.target.style.backgroundColor = '#163626';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (pagination.hasNextPage) {
                        e.target.style.backgroundColor = '#1A3E2A';
                      }
                    }}
                  >
                    Next →
                  </button>

                  {/* Last Page */}
                  <button
                    onClick={() => setCurrentPage(pagination.totalPages)}
                    disabled={!pagination.hasNextPage}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: pagination.hasNextPage ? '#1A3E2A' : '#E3DFD3',
                      color: pagination.hasNextPage ? 'white' : '#A5A89B',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: pagination.hasNextPage ? 'pointer' : 'not-allowed',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (pagination.hasNextPage) {
                        e.target.style.backgroundColor = '#163626';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (pagination.hasNextPage) {
                        e.target.style.backgroundColor = '#1A3E2A';
                      }
                    }}
                  >
                    Last
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AvailabilityBlockManager;